import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import WalletConnector from './WalletConnector';
import { formatNumber, formatAddress, ACHIEVEMENT_LEVELS, ACTIVITY_TYPES, PLANS } from '../utils/constants';
import '../styles/Dashboard.css';

const Dashboard = ({
  userInfo,
  userIncomes,
  tokenBalance,
  isNewUser,
  account,
  stakingContract,
  refreshData,
  selectedNetwork,
  onWalletConnect,
  onWalletDisconnect,
  onWalletError,
  onChainChanged,
  setActiveTab
}) => {
  const [tokenPrice, setTokenPrice] = useState('2.00');
  const [totalWithdrawn, setTotalWithdrawn] = useState('0');
  const [availableROI, setAvailableROI] = useState('0');
  const [recentActivities, setRecentActivities] = useState([]);
  const [totalTeamCount, setTotalTeamCount] = useState('0');
  const [directBusiness, setDirectBusiness] = useState('0');
  const [loading, setLoading] = useState(false);
  const [achievementLevel, setAchievementLevel] = useState(0);
  const [userInvestments, setUserInvestments] = useState([]);
  const [referralInfo, setReferralInfo] = useState(null);
  const [capitalStatus, setCapitalStatus] = useState([]);
  const [withdrawing, setWithdrawing] = useState({});
  const [planDetails, setPlanDetails] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [riaStatus, setRiaStatus] = useState(null);
  const [claimableReferral, setClaimableReferral] = useState('0');
  const [claimingReferral, setClaimingReferral] = useState(false);

  // Notification functions
  const addNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    const notification = { id, message, type };
    
    setNotifications(prev => [...prev, notification]);
    
    if (duration > 0) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, duration);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Token price loading function (copied exactly from StakingForm)
  const loadTokenPrice = async () => {
    try {
      const price = await stakingContract.getTokenPrice();
      setTokenPrice(formatNumber(parseFloat(price.toString()) / 1e18, 2));
    } catch (error) {
      console.error('Failed to get token price:', error);
      setTokenPrice('2.00');
    }
  };

  // Load token price (copied exactly from StakingForm pattern)
  useEffect(() => {
    if (stakingContract) {
      loadTokenPrice();
    }
  }, [stakingContract]);

  useEffect(() => {
    if (stakingContract && userInfo && userIncomes) {
      loadPlanDetails();
      loadRealData();
    }
  }, [stakingContract, userInfo, userIncomes]);

  useEffect(() => {
    if (stakingContract && userInfo && userIncomes) {
      loadRealData();
    }
  }, [refreshData]);

  useEffect(() => {
    // Extract referral info from URL for new users
    const urlParams = new URLSearchParams(window.location.search);
    const refId = urlParams.get('ref');
    if (refId && stakingContract && !userInfo) {
      loadReferralInfo(refId);
    }
  }, [stakingContract, userInfo]);

  const loadPlanDetails = async () => {
    if (!stakingContract) return;
    
    try {
      const plans = {};
      // Load details for plans 1-14
      for (let i = 1; i <= 14; i++) {
        try {
          const planInfo = await stakingContract.getPlanInfo(i);
          plans[i] = {
            minAmount: planInfo[0],
            maxAmount: planInfo[1], 
            roiPercentage: planInfo[2],
            duration: planInfo[3],
            withdrawFrequency: planInfo[4],
            capitalWithdrawTime: planInfo[5],
            referralCommission: planInfo[6],
            requiresReferrals: planInfo[7],
            isActive: planInfo[8],
            capitalWithdrawAllowed: planInfo[9]
          };
        } catch (error) {
          console.error(`Failed to load plan ${i}:`, error);
        }
      }
      setPlanDetails(plans);
    } catch (error) {
      console.error('Failed to load plan details:', error);
    }
  };

  const loadReferralInfo = async (referralId) => {
    try {
      // Check if referral ID exists
      const exists = await stakingContract.userIdExists(referralId);
      if (exists) {
        // Get referral wallet address (usersById public getter was removed from the contract)
        const referralWallet = await stakingContract.getUserAddressById(referralId);
        setReferralInfo({
          id: referralId,
          address: referralWallet,
          exists: true
        });
      }
    } catch (error) {
      console.error('Failed to load referral info:', error);
      setReferralInfo(null);
    }
  };

  const getPlanInfo = (planId) => {
    return PLANS.find(plan => plan.id === parseInt(planId)) || null;
  };

  const isCapitalWithdrawAllowed = (planId) => {
    // Plans that allow capital withdrawal: 2, 3, 5, 6, 7, 8
    return [2, 3, 5, 6, 7, 8].includes(parseInt(planId));
  };

  const isEndOnlyPlan = (planId) => {
    // Plans with end-only withdrawals: 1, 6, 7, 8, 9
    return [1, 6, 7, 8, 9].includes(parseInt(planId));
  };

  const canWithdrawROI = (investment) => {
    const planId = parseInt(investment.planId);
    const planInfo = getPlanInfo(planId);
    
    if (!planInfo || !investment.isActive) return false;
    
    // For end-only plans, check if the duration has passed
    if (isEndOnlyPlan(planId)) {
      const now = Date.now();
      const startTime = investment.startTime.getTime();
      const durationMs = planInfo.frequency === "End Only" ? 
        (planId === 1 ? 60 * 30 * 24 * 60 * 60 * 1000 : // 60 months for plan 1
         planId === 6 || planId === 7 ? 36 * 30 * 24 * 60 * 60 * 1000 : // 36 months for plans 6,7
         1825 * 24 * 60 * 60 * 1000) : 0; // 1825 days for plans 8,9
      
      return now >= (startTime + durationMs);
    }
    
    // For regular withdrawal plans (2, 3, 4, 5, 10), ROI can be withdrawn anytime
    return true;
  };

  const calculateCapitalStatus = async (formattedInvestments) => {
    const capitalStatusArray = await Promise.all(
      formattedInvestments.map(async (inv, index) => {
        const planId = parseInt(inv.planId);
        
        if (inv.capitalWithdrawn || !inv.isActive) {
          return {
            index,
            isAvailable: false,
            timeRemaining: 0,
            status: inv.capitalWithdrawn ? 'withdrawn' : '',
            isEligible: false
          };
        }

        // Check if plan allows capital withdrawal
        if (!isCapitalWithdrawAllowed(planId)) {
          return {
            index,
            isAvailable: false,
            timeRemaining: 0,
            status: 'not_eligible',
            isEligible: false
          };
        }

        // Calculate unlock time for eligible plans
        try {
          const planInfo = planDetails[planId];
          if (!planInfo) {
            return {
              index,
              isAvailable: false,
              timeRemaining: 0,
              status: 'error',
              isEligible: false
            };
          }

          const capitalWithdrawTime = planInfo.capitalWithdrawTime;
          const unlockTime = inv.startTime.getTime() + (capitalWithdrawTime.toNumber() * 1000);
          const now = Date.now();
          
          return {
            index,
            isAvailable: now >= unlockTime,
            timeRemaining: Math.max(0, unlockTime - now),
            status: now >= unlockTime ? 'available' : 'locked',
            unlockTime: new Date(unlockTime),
            isEligible: true
          };
        } catch (error) {
          console.error(`Failed to calculate capital status for investment ${index}:`, error);
          return {
            index,
            isAvailable: false,
            timeRemaining: 0,
            status: 'error',
            isEligible: false
          };
        }
      })
    );
    return capitalStatusArray;
  };

  const loadRealData = async () => {
    try {
      setLoading(true);

      // Get real user achievement level
      try {
        const level = await stakingContract.getAchievementLevel(userInfo.id);
        setAchievementLevel(parseInt(level.toString()));
      } catch (error) {
        console.error('Failed to get achievement level:', error);
        setAchievementLevel(0);
      }

      // Get RIA (Retopup) status + claimable (vested) referral income
      try {
        const ria = await stakingContract.getRIAStatus(userInfo.id);
        setRiaStatus({
          riaModeActive: ria[0],
          riaOccurred: ria[1],
          riaCount: ria[2].toString(),
          accumulatedForRIA: ethers.utils.formatEther(ria[3]),
          packageCost: ethers.utils.formatEther(ria[4]),
          entryThreshold: ethers.utils.formatEther(ria[5]),
          totalIncomeEarned: ethers.utils.formatEther(ria[6])
        });
      } catch (error) {
        console.error('Failed to get RIA status:', error);
        setRiaStatus(null);
      }

      try {
        const claimable = await stakingContract.claimableReferral(userInfo.id);
        setClaimableReferral(ethers.utils.formatEther(claimable));
      } catch (error) {
        console.error('Failed to get claimable referral:', error);
        setClaimableReferral('0');
      }

      // Get user investments for ROI calculations and capital status
      try {
        const investments = await stakingContract.getUserInvestments(userInfo.id);
        const formattedInvestments = investments.map((inv, index) => ({
          index,
          planId: inv[0].toString(),
          amount: ethers.utils.formatEther(inv[1]),
          startTime: new Date(inv[2].toNumber() * 1000),
          lastWithdraw: new Date(inv[3].toNumber() * 1000),
          totalWithdrawn: ethers.utils.formatEther(inv[4]),
          capitalWithdrawn: inv[5],
          isActive: inv[6]
        }));
        setUserInvestments(formattedInvestments);

        // Calculate capital status using the new function
        if (Object.keys(planDetails).length > 0) {
          const capitalStatusArray = await calculateCapitalStatus(formattedInvestments);
          setCapitalStatus(capitalStatusArray);
        }

        // Calculate real available ROI from all active investments
        let totalAvailableROI = 0;
        for (let i = 0; i < formattedInvestments.length; i++) {
          if (formattedInvestments[i].isActive) {
            try {
              const roi = await stakingContract.calculateROI(userInfo.id, i);
              totalAvailableROI += parseFloat(ethers.utils.formatEther(roi));
            } catch (error) {
              console.error(`Failed to calculate Rewards for investment ${i}:`, error);
            }
          }
        }
        setAvailableROI(formatNumber(totalAvailableROI, 2));

        // Calculate real total withdrawn from investments
        const totalWithdrawnAmount = formattedInvestments.reduce((sum, inv) => {
          return sum + parseFloat(inv.totalWithdrawn);
        }, 0);
        setTotalWithdrawn(formatNumber(totalWithdrawnAmount, 2));
      } catch (error) {
        console.error('Failed to load investments:', error);
        setUserInvestments([]);
        setCapitalStatus([]);
        setAvailableROI('0');
        setTotalWithdrawn('0');
      }

      // Get real total team count from contract
      try {
        console.log('Attempting to get total team count from contract...');
        
        if (stakingContract.getTotalTeamCount) {
          const totalTeamFromContract = await stakingContract.getTotalTeamCount(userInfo.id);
          const realTeamCount = parseInt(totalTeamFromContract.toString());
          setTotalTeamCount(realTeamCount.toString());
          console.log('Got real team count from contract:', realTeamCount);
        } else {
          console.log('getTotalTeamCount function not found in contract');
          setTotalTeamCount(userInfo.directReferrals || '0');
        }
      } catch (error) {
        console.error('Failed to get team count from contract:', error);
        setTotalTeamCount(userInfo.directReferrals || '0');
      }

      // Calculate real direct business using team breakdown
      let realDirectBusiness = 0;
      try {
        if (userInfo.directReferrals && parseInt(userInfo.directReferrals) > 0) {
          const teamBreakdown = await stakingContract.getTeamBreakdown(userInfo.id);
          // teamBreakdown returns: (levelCounts, levelVolumes, totalTeamMembers, totalTeamVolume)
          const levelVolumes = teamBreakdown[1]; // Array of volumes for each level
          realDirectBusiness = parseFloat(ethers.utils.formatEther(levelVolumes[0])); // Level 1 volume (direct business)
        }
      } catch (error) {
        console.error('Failed to get team breakdown for direct business:', error);
        realDirectBusiness = 0;
      }
      setDirectBusiness(formatNumber(realDirectBusiness, 2));

      // Create activities from available data
      const activities = [];
      if (userIncomes) {
        const now = Date.now();

        if (userIncomes.roi && parseFloat(userIncomes.roi) > 0) {
          activities.push({
            timestamp: new Date(now - 3600000),
            amount: (parseFloat(userIncomes.roi) * 1e18).toString(),
            type: 2,
            level: '0',
            fromUserId: '0'
          });
        }

        if (userIncomes.referral && parseFloat(userIncomes.referral) > 0) {
          activities.push({
            timestamp: new Date(now - 86400000),
            amount: (parseFloat(userIncomes.referral) * 1e18).toString(),
            type: 3,
            level: '1',
            fromUserId: userInfo.id || '0'
          });
        }

        if (userIncomes.level && parseFloat(userIncomes.level) > 0) {
          activities.push({
            timestamp: new Date(now - 172800000),
            amount: (parseFloat(userIncomes.level) * 1e18).toString(),
            type: 4,
            level: '2',
            fromUserId: userInfo.id || '0'
          });
        }

        if (userInfo.totalStaked && parseFloat(userInfo.totalStaked) > 0) {
          activities.push({
            timestamp: new Date(now - 259200000),
            amount: (parseFloat(userInfo.totalStaked) * 1e18).toString(),
            type: 1,
            level: '0',
            fromUserId: '0'
          });
        }
      }

      activities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setRecentActivities(activities);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update capital status when plan details are loaded
  useEffect(() => {
    if (Object.keys(planDetails).length > 0 && userInvestments.length > 0) {
      calculateCapitalStatus(userInvestments).then(setCapitalStatus);
    }
  }, [planDetails, userInvestments]);

  // Capital withdrawal function
  const handleCapitalWithdraw = async (investmentIndex) => {
    if (!stakingContract || !userInfo) return;

    const investment = userInvestments[investmentIndex];
    const capitalInfo = capitalStatus[investmentIndex];
    const planInfo = getPlanInfo(investment.planId);
    
    // Check eligibility
    if (!capitalInfo || !capitalInfo.isEligible) {
      addNotification('Capital withdrawal is not available for this plan.', 'warning');
      return;
    }

    if (!capitalInfo.isAvailable) {
      const timeRemaining = formatTimeRemaining(capitalInfo.timeRemaining);
      addNotification(`Capital is still locked. Available in: ${timeRemaining}`, 'info');
      return;
    }

    // Confirmation
    const confirmationMessage = `Are you sure you want to withdraw your capital of ${formatNumber(investment.amount, 2)} USDT from ${planInfo?.name || 'Plan ' + investment.planId}?\n\nThis action will permanently close your investment and stop all future returns.`;
    
    if (!window.confirm(confirmationMessage)) return;

    try {
      setWithdrawing(prev => ({ ...prev, [investmentIndex]: true }));

      addNotification('Capital withdrawal transaction submitted. Please wait for confirmation...', 'info');

      const tx = await stakingContract.withdrawCapital(investmentIndex);
      console.log('Capital withdrawal transaction sent:', tx.hash);
      
      await tx.wait();
      console.log('Capital withdrawal confirmed');

      addNotification(
        `Successfully withdrew capital of ${formatNumber(investment.amount, 2)} USDT from ${planInfo?.name || 'Plan ' + investment.planId}!`, 
        'success'
      );

      // Refresh data after successful withdrawal
      if (refreshData) {
        await refreshData();
      }
      await loadRealData();
      
    } catch (error) {
      console.error('Capital withdrawal failed:', error);
      
      if (error.code === 4001) {
        addNotification('Transaction was cancelled by user.', 'warning');
      } else if (error.message.includes('Lock period not over')) {
        addNotification('Capital is still in lock period. Please wait for the unlock time.', 'warning');
      } else if (error.message.includes('Already withdrawn')) {
        addNotification('Capital has already been withdrawn from this investment.', 'info');
      } else if (error.message.includes('Capital withdrawal not allowed')) {
        addNotification('Capital withdrawal is not permitted for this plan type.', 'warning');
      } else {
        addNotification('Capital withdrawal failed. Please try again or contact support if the issue persists.', 'error');
      }
    } finally {
      setWithdrawing(prev => ({ ...prev, [investmentIndex]: false }));
    }
  };

  // ROI withdrawal function
  const handleROIWithdraw = async (investmentIndex) => {
    if (!stakingContract || !userInfo) return;

    const investment = userInvestments[investmentIndex];
    const planInfo = getPlanInfo(investment.planId);
    
    if (!canWithdrawROI(investment)) {
      if (isEndOnlyPlan(parseInt(investment.planId))) {
        addNotification(
          `This is an end-only plan. ${planInfo?.id === 1 ? '2x Payout' : 'Total accumulated rewards'} can only be withdrawn after the ${planInfo?.duration || 'investment period'} is complete.`,
          'warning'
        );
      } else {
        addNotification('Reward withdrawal not available for this investment.', 'error');
      }
      return;
    }

    try {
      setWithdrawing(prev => ({ ...prev, [`roi_${investmentIndex}`]: true }));

      addNotification('Reward withdrawal transaction submitted. Please wait for confirmation...', 'info');

      const tx = await stakingContract.withdrawROI(investmentIndex);
      console.log('Reward withdrawal transaction sent:', tx.hash);
      
      await tx.wait();
      console.log('Reward withdrawal confirmed');

      const currentAvailableROI = parseFloat(availableROI);
      addNotification(
        `Successfully withdrew ${formatNumber(currentAvailableROI, 6)} USDT from ${planInfo?.name || 'Plan ' + investment.planId}!`, 
        'success'
      );
      
      // Refresh data after successful withdrawal
      if (refreshData) {
        await refreshData();
      }
      await loadRealData();
      
    } catch (error) {
      console.error('Reward withdrawal failed:', error);
      
      if (error.code === 4001) {
        addNotification('Transaction was cancelled by user.', 'warning');
      } else if (error.message.includes('No reward available')) {
        addNotification('No rewards are currently available for withdrawal.', 'info');
      } else if (error.message.includes('Referral requirement not met')) {
        addNotification('Referral requirements must be maintained for withdrawals from this plan.', 'warning');
      } else {
        addNotification('Reward withdrawal failed. Please try again or contact support if the issue persists.', 'error');
      }
    } finally {
      setWithdrawing(prev => ({ ...prev, [`roi_${investmentIndex}`]: false }));
    }
  };

  // Claim vested direct-referral commission. If the user is in RIA mode the contract
  // routes the claimed amount toward the next retopup instead of transferring it out.
  const handleClaimReferral = async () => {
    if (!stakingContract || !userInfo) return;

    if (parseFloat(claimableReferral) <= 0) {
      addNotification('No vested referral income available to claim yet.', 'info');
      return;
    }

    try {
      setClaimingReferral(true);
      addNotification('Referral claim transaction submitted. Please wait for confirmation...', 'info');

      const tx = await stakingContract.claimReferral();
      await tx.wait();

      addNotification(
        riaStatus?.riaModeActive
          ? 'Claimed! Your vested referral income was routed toward your next RIA retopup.'
          : `Successfully claimed ${formatNumber(claimableReferral, 4)} USDT of vested referral income!`,
        'success'
      );

      if (refreshData) await refreshData();
      await loadRealData();
    } catch (error) {
      console.error('Referral claim failed:', error);
      if (error.code === 4001) {
        addNotification('Transaction was cancelled by user.', 'warning');
      } else if (error.message && error.message.includes('Nothing to claim')) {
        addNotification('No vested referral income available to claim yet.', 'info');
      } else {
        addNotification('Referral claim failed. Please try again.', 'error');
      }
    } finally {
      setClaimingReferral(false);
    }
  };

  const getAchievementInfo = (level) => {
    return ACHIEVEMENT_LEVELS[level] || ACHIEVEMENT_LEVELS[0];
  };

  const generateReferralLink = () => {
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?ref=${userInfo?.id}`;
  };

  const copyReferralLink = () => {
    const referralLink = generateReferralLink();
    navigator.clipboard.writeText(referralLink).then(() => {
      addNotification('Referral link copied to clipboard!', 'success');
    }).catch(err => {
      console.error('Failed to copy referral link:', err);
      addNotification('Failed to copy referral link. Please try again.', 'error');
    });
  };

  const handleWithdraw = () => {
    if (setActiveTab && typeof setActiveTab === 'function') {
      setActiveTab('investments');
    } else {
      addNotification('Please go to the Investments tab to withdraw from specific investments', 'info');
    }
  };

  const handleStartInvesting = () => {
    if (setActiveTab && typeof setActiveTab === 'function') {
      setActiveTab('stake');
    } else {
      addNotification('Please go to the Stake tab to start investing', 'info');
    }
  };

  const handleUpdateReturns = () => {
    if (refreshData) {
      refreshData();
    }
  };

  const formatActivityTime = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  const formatTimeRemaining = (milliseconds) => {
    if (milliseconds <= 0) return 'Available now';
    
    const minutes = Math.floor(milliseconds / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  // Calculate network earnings (referral + level income)
  const calculateNetworkEarnings = () => {
    const referralIncome = parseFloat(userIncomes?.referral || 0);
    const levelIncome = parseFloat(userIncomes?.level || 0);
    return referralIncome + levelIncome;
  };

  const getInvestmentDisplayInfo = (investment) => {
    const planId = parseInt(investment.planId);
    const planInfo = getPlanInfo(planId);
    const isEndOnly = isEndOnlyPlan(planId);
    const canWithdraw = canWithdrawROI(investment);
    
    return {
      planInfo,
      isEndOnly,
      canWithdraw,
      withdrawType: isEndOnly ? (planId === 1 ? '2x Payout' : 'Total Accumulated') : 'Regular Rewards'
    };
  };

  const achievementInfo = getAchievementInfo(achievementLevel);

  return (
    <div className="dashboard">
      {/* Notifications */}
      <div className="dashboard-notifications">
        {notifications.map(notification => (
          <div 
            key={notification.id} 
            className={`dashboard-notification dashboard-${notification.type}-notification`}
          >
            <div className="notification-content">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {notification.type === 'success' && (
                  <>
                    <path d="M9 12l2 2 4-4"/>
                    <circle cx="12" cy="12" r="10"/>
                  </>
                )}
                {notification.type === 'error' && (
                  <>
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </>
                )}
                {notification.type === 'warning' && (
                  <>
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </>
                )}
                {notification.type === 'info' && (
                  <>
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </>
                )}
              </svg>
              <span>{notification.message}</span>
            </div>
            <button
              className="notification-close"
              onClick={() => removeNotification(notification.id)}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Dashboard Header */}
      <div className="dashboard-header">
        <h1 className="dashboard-title">
          <span className="title-icon">AI</span>
          Trail AI Dashboard
        </h1>
        <p className="dashboard-subtitle">Intelligent Staking • Advanced Analytics • Future-Ready</p>

        {/* Top Right Info Cards */}
        <div className="top-right-info">
          <div className="mini-info-card trail-price">
            <span className="mini-icon">TRAIL Price: </span>
            <span className="mini-value">${tokenPrice}</span>
          </div>
          <div className="mini-info-card wallet-balance">
            <span className="mini-icon">BALANCE: </span>
            <span className="mini-value">{formatNumber(tokenBalance, 2)} TRAIL (${formatNumber(tokenBalance * parseFloat(tokenPrice), 2)})</span>
          </div>
        </div>
      </div>

      {/* Wallet Connection */}
      <div className="wallet-connection-section">
        <WalletConnector
          account={account}
          onConnect={onWalletConnect}
          onDisconnect={onWalletDisconnect}
          onError={onWalletError}
          onChainChanged={onChainChanged}
          requiredChainId={selectedNetwork === 'opBNB' ? 204 : 5611}
          buttonClassName="compact-wallet-button"
        />
      </div>

      {/* Welcome User Box */}
      {userInfo && (
        <div className="welcome-user-box">
          <div className="user-avatar-section">
            <div className="user-avatar">👤</div>
            <div className="user-rank">
              <span className="rank-badge" style={{ backgroundColor: achievementInfo.color }}>
                {achievementInfo.name}
              </span>
            </div>
          </div>
          <div className="user-id-section">
            <div className="user-id-display">USER ID: #{userInfo.id}</div>
            
            {/* Show only sponsor ID for existing users (no address) */}
            {userInfo.referrerId && userInfo.referrerId !== '0' && userInfo.referrerId !== 0 && (
              <div className="sponsor-id-display">SPONSOR ID: #{userInfo.referrerId}</div>
            )}
          </div>
        </div>
      )}

      {/* Enhanced welcome content for new users */}
      {(isNewUser || (!userInfo && account)) && (
        <div className="welcome-section">
          <div className="welcome-card">
            <div className="welcome-header">
              <div className="welcome-icon">🚀</div>
              <div>
                <h2>Welcome to Trail AI Staking</h2>
                <p>Your intelligent investment journey begins here</p>
              </div>
            </div>

            {/* Show upline info for new users coming via referral */}
            {referralInfo && referralInfo.exists && (
              <div className="new-user-upline-section">
                <h3>🎯 You've been invited by:</h3>
                <div className="upline-info-card">
                  <div className="upline-details">
                    <div className="upline-id">SPONSOR ID: #{referralInfo.id}</div>
                    <div className="upline-address">
                      SPONSOR: {formatAddress(referralInfo.address)}
                    </div>
                  </div>
                  <div className="invitation-badge">
                    <span>✨ Verified Invitation</span>
                  </div>
                </div>
              </div>
            )}

            <div className="welcome-stats">
              <div className="welcome-stat">
                <div className="stat-icon">🧠</div>
                <div>
                  <div className="stat-title">AI-Powered Plans</div>
                  <div className="stat-desc">14 intelligent staking strategies with optimized returns</div>
                </div>
              </div>
              <div className="welcome-stat">
                <div className="stat-icon">💎</div>
                <div>
                  <div className="stat-title">Multi-Level Rewards</div>
                  <div className="stat-desc">Earn up to 20 levels of commission from your network</div>
                </div>
              </div>
              <div className="welcome-stat">
                <div className="stat-icon">⚡</div>
                <div>
                  <div className="stat-title">Instant Payouts</div>
                  <div className="stat-desc">Real-time reward distribution to your wallet</div>
                </div>
              </div>
              <div className="welcome-stat">
                <div className="stat-icon">📊</div>
                <div>
                  <div className="stat-title">Smart Analytics</div>
                  <div className="stat-desc">Advanced tracking and performance insights</div>
                </div>
              </div>
              <div className="welcome-stat">
                <div className="stat-icon">🔒</div>
                <div>
                  <div className="stat-title">Secure & Transparent</div>
                  <div className="stat-desc">Blockchain-verified smart contracts</div>
                </div>
              </div>
              <div className="welcome-stat">
                <div className="stat-icon">🌟</div>
                <div>
                  <div className="stat-title">Achievement System</div>
                  <div className="stat-desc">Unlock ranks from Bronze to Crown Diamond</div>
                </div>
              </div>
            </div>

            {/* Start Investment Button for new users */}
            <div className="start-invest-section">
              <button 
                className="start-invest-btn" 
                onClick={handleStartInvesting}
              >
                <span className="btn-icon">💰</span>
                Start Your Investment Journey
              </button>
              <p className="start-invest-desc">
                Choose from 14 plans ranging from $25 to $250,000
                {referralInfo && referralInfo.exists && (
                  <span className="referral-bonus-text">
                    <br />🎁 <strong>Bonus:</strong> Join through verified invitation for enhanced rewards!
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="ai-loading-spinner"></div>
          <p>Analyzing your data...</p>
        </div>
      )}

      {/* Show loading state for unconnected users */}
      {!isNewUser && !userInfo && account && !loading && (
        <div className="loading-container">
          <div className="ai-loading-spinner"></div>
          <p>Loading user data...</p>
        </div>
      )}

      {/* Main Dashboard Content */}
      {userInfo && !loading && (
        <div className="dashboard-content">
          {/* Main Stats Grid */}
          <div className="stats-grid">
            {/* Total Staked */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">💰</div>
                <div className="stat-info">
                  <div className="stat-label">Total Staked</div>
                  <div className="stat-value">{formatNumber(userInfo.totalStaked || 0, 2)}</div>
                  <div className="stat-unit">USDT</div>
                </div>
              </div>
            </div>

            {/* Total Team Count */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <div className="stat-label">Total Team</div>
                  <div className="stat-value">{totalTeamCount}</div>
                  <div className="stat-unit">Members</div>
                </div>
              </div>
            </div>

            {/* Direct Referrals */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">🤝</div>
                <div className="stat-info">
                  <div className="stat-label">Direct Referrals</div>
                  <div className="stat-value">{userInfo.directReferrals || 0}</div>
                  <div className="stat-unit">Users</div>
                </div>
              </div>
            </div>

            {/* Direct Business */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">📊</div>
                <div className="stat-info">
                  <div className="stat-label">Direct Business</div>
                  <div className="stat-value">{directBusiness}</div>
                  <div className="stat-unit">USDT</div>
                </div>
              </div>
            </div>

            {/* Team Volume */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">📈</div>
                <div className="stat-info">
                  <div className="stat-label">Team Volume</div>
                  <div className="stat-value">{formatNumber(userInfo.teamVolume || 0, 0)}</div>
                  <div className="stat-unit">USDT</div>
                </div>
              </div>
            </div>

            {/* Available ROI */}
            <div className="stat-card income-card">
              <div className="stat-header">
                <div className="stat-icon">💵</div>
                <div className="stat-info">
                  <div className="stat-label">Available Rewards</div>
                  <div className="stat-value">{availableROI}</div>
                  <div className="stat-unit">USDT</div>
                </div>
              </div>
            </div>

            {/* Total Income Earned */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">🏆</div>
                <div className="stat-info">
                  <div className="stat-label">Total Earned</div>
                  <div className="stat-value">{formatNumber(userIncomes?.total || 0, 2)}</div>
                  <div className="stat-unit">USDT</div>
                </div>
              </div>
            </div>

            {/* Total Withdrawn */}
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">💸</div>
                <div className="stat-info">
                  <div className="stat-label">Total Withdrawn</div>
                  <div className="stat-value">{totalWithdrawn}</div>
                  <div className="stat-unit">USDT</div>
                </div>
              </div>
            </div>
          </div>

          {/* Vested Referral Income + RIA (Retopup) Status */}
          <div className="main-content-grid">
            {/* Vested Referral Income */}
            <div className="content-card referral-vesting">
              <div className="card-header">
                <h3>Referral Income (Vested)</h3>
                <div className="total-income">
                  <span className="total-value">{formatNumber(claimableReferral, 4)} USDT</span>
                </div>
              </div>
              <div className="income-breakdown">
                <p className="vesting-note">
                  Direct referral commissions vest over 3 months (1/3 released each month).
                  Claim your released portion below.
                </p>
                <div className="income-item">
                  <div className="income-icon referral">👥</div>
                  <div className="income-details">
                    <div className="income-label">Available to Claim</div>
                    <div className="income-value">{formatNumber(claimableReferral, 4)} USDT</div>
                  </div>
                </div>
                {riaStatus?.riaModeActive && (
                  <div className="end-only-status">
                    <span className="info-icon">🔁</span>
                    <span className="info-text">
                      You are in RIA mode — claimed referral income is routed to your next retopup.
                    </span>
                  </div>
                )}
                <button
                  className="action-btn roi-withdraw-btn"
                  onClick={handleClaimReferral}
                  disabled={claimingReferral || parseFloat(claimableReferral) <= 0}
                >
                  {claimingReferral ? <span>Claiming...</span> : <span>Claim Vested Referral</span>}
                </button>
              </div>
            </div>

            {/* RIA / Retopup Status */}
            {riaStatus && parseFloat(riaStatus.packageCost) > 0 && (
              <div className="content-card ria-status">
                <div className="card-header">
                  <h3>RIA / Retopup Status</h3>
                  <span
                    className="rank-badge"
                    style={{ backgroundColor: riaStatus.riaModeActive ? '#a855f7' : '#94a3b8' }}
                  >
                    {riaStatus.riaModeActive ? 'RIA Active' : 'Inactive'}
                  </span>
                </div>
                <div className="income-breakdown">
                  <div className="income-item">
                    <div className="income-icon roi">🏆</div>
                    <div className="income-details">
                      <div className="income-label">Total Income Earned</div>
                      <div className="income-value">{formatNumber(riaStatus.totalIncomeEarned, 2)} USDT</div>
                    </div>
                  </div>
                  <div className="income-item">
                    <div className="income-icon level">🎯</div>
                    <div className="income-details">
                      <div className="income-label">RIA Entry Threshold</div>
                      <div className="income-value">{formatNumber(riaStatus.entryThreshold, 2)} USDT</div>
                    </div>
                  </div>
                  <div className="income-item">
                    <div className="income-icon referral">🔁</div>
                    <div className="income-details">
                      <div className="income-label">Accumulated for Next Retopup</div>
                      <div className="income-value">
                        {formatNumber(riaStatus.accumulatedForRIA, 2)} / {formatNumber(riaStatus.packageCost, 2)} USDT
                      </div>
                    </div>
                  </div>
                  <div className="income-item">
                    <div className="income-icon level">✅</div>
                    <div className="income-details">
                      <div className="income-label">Retopups Completed</div>
                      <div className="income-value">{riaStatus.riaCount}</div>
                    </div>
                  </div>
                  {riaStatus.riaOccurred && (
                    <div className="end-only-status">
                      <span className="info-icon">⚠️</span>
                      <span className="info-text">
                        A retopup has fired — capital/total returns are forfeited on plans 1–8 per the RIA rules.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Investment Management Section */}
          {userInvestments.length > 0 && (
            <div className="content-card investment-management">
              <div className="card-header">
                <h3>Investment Management</h3>
                <div className="investment-count">{userInvestments.length} Active</div>
              </div>

              <div className="investments-list">
                {userInvestments.map((investment, index) => {
                  const capitalInfo = capitalStatus[index];
                  const displayInfo = getInvestmentDisplayInfo(investment);
                  
                  return (
                    <div key={index} className="investment-item">
                      <div className="investment-info">
                        <div className="investment-header">
                          <div className="plan-badge">Plan {investment.planId}</div>
                          <div className="investment-amount">{formatNumber(investment.amount)} USDT</div>
                        </div>
                        <div className="investment-status">
                          <span className={`status-badge ${investment.isActive ? 'active' : ''}`}>
                            {investment.isActive ? 'Active' : ''}
                          </span>
                          <span className="investment-date">
                            Started: {investment.startTime.toLocaleDateString()}
                          </span>
                          {displayInfo.planInfo && (
                            <span className="plan-type">
                              {displayInfo.planInfo.name} - {displayInfo.withdrawType}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="investment-actions">
                        {/* ROI Withdrawal */}
                        {investment.isActive && (
                          <button 
                            className={`action-btn roi-withdraw-btn ${!displayInfo.canWithdraw ? 'disabled' : ''}`}
                            onClick={() => handleROIWithdraw(index)}
                            disabled={withdrawing[`roi_${index}`] || !displayInfo.canWithdraw || parseFloat(availableROI) === 0}
                          >
                            {withdrawing[`roi_${index}`] ? (
                              <span>Withdrawing...</span>
                            ) : displayInfo.isEndOnly ? (
                              <span>Withdraw {displayInfo.withdrawType}</span>
                            ) : (
                              <span>Withdraw Rewards</span>
                            )}
                          </button>
                        )}

                        {/* End-only plan status */}
                        {displayInfo.isEndOnly && !displayInfo.canWithdraw && (
                          <div className="end-only-status">
                            <span className="info-icon">⏰</span>
                            <span className="info-text">
                              {displayInfo.withdrawType} available after {displayInfo.planInfo?.duration}
                            </span>
                          </div>
                        )}

                        {/* Capital Withdrawal - Only show if eligible */}
                        {capitalInfo && capitalInfo.isEligible && (
                          <div className="capital-section">
                            {capitalInfo.status === 'available' && (
                              <button 
                                className="action-btn capital-withdraw-btn"
                                onClick={() => handleCapitalWithdraw(index)}
                                disabled={withdrawing[index]}
                              >
                                {withdrawing[index] ? (
                                  <span>Withdrawing...</span>
                                ) : (
                                  <span>Withdraw Capital</span>
                                )}
                              </button>
                            )}
                            
                            {capitalInfo.status === 'locked' && (
                              <div className="capital-locked">
                                <span className="lock-icon">🔒</span>
                                <span className="lock-text">
                                  Capital available in: {formatTimeRemaining(capitalInfo.timeRemaining)}
                                </span>
                              </div>
                            )}
                            
                            {capitalInfo.status === 'withdrawn' && (
                              <div className="capital-withdrawn">
                                <span className="withdrawn-icon">✅</span>
                                <span className="withdrawn-text">Capital Withdrawn</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="action-btn withdraw-btn" onClick={handleWithdraw}>
              <span className="btn-icon">💸</span>
              Manage Investments
            </button>
          </div>

          {/* Main Content Grid */}
          <div className="main-content-grid">
            {/* Income Overview */}
            <div className="content-card income-overview">
              <div className="card-header">
                <h3>AI Income Analytics</h3>
                <div className="total-income">
                  <span className="total-value">{formatNumber(userIncomes?.total || 0, 2)} USDT</span>
                </div>
              </div>

              <div className="income-breakdown">
                <div className="income-item">
                  <div className="income-icon roi">📈</div>
                  <div className="income-details">
                    <div className="income-label">Staking Income</div>
                    <div className="income-value">{formatNumber(userIncomes?.roi || 0, 2)} USDT</div>
                  </div>
                  <div className="income-percentage">
                    {userIncomes?.total > 0
                      ? `${(((userIncomes.roi || 0) / userIncomes.total) * 100).toFixed(1)}%`
                      : '0%'
                    }
                  </div>
                </div>

                <div className="income-item">
                  <div className="income-icon referral">👥</div>
                  <div className="income-details">
                    <div className="income-label">Referral Income</div>
                    <div className="income-value">{formatNumber(userIncomes?.referral || 0, 2)} USDT</div>
                  </div>
                  <div className="income-percentage">
                    {userIncomes?.total > 0
                      ? `${(((userIncomes.referral || 0) / userIncomes.total) * 100).toFixed(1)}%`
                      : '0%'
                    }
                  </div>
                </div>

                <div className="income-item">
                  <div className="income-icon level">🏆</div>
                  <div className="income-details">
                    <div className="income-label">Level Income</div>
                    <div className="income-value">{formatNumber(userIncomes?.level || 0, 2)} USDT</div>
                  </div>
                  <div className="income-percentage">
                    {userIncomes?.total > 0
                      ? `${(((userIncomes.level || 0) / userIncomes.total) * 100).toFixed(1)}%`
                      : '0%'
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Referral Program */}
            <div className="content-card referral-program">
              <div className="card-header">
                <h3>Network Program</h3>
                <div className="referral-id">
                  <span className="ref-label">ID:</span>
                  <span className="ref-value">{userInfo.id}</span>
                </div>
              </div>

              <div className="referral-link-section">
                <div className="referral-link-container">
                  <div className="referral-link">{generateReferralLink()}</div>
                  <button className="copy-link-btn" onClick={copyReferralLink}>
                    <span className="btn-icon">📋</span>
                    Copy
                  </button>
                </div>
              </div>

              <div className="referral-stats">
                <div className="ref-stat">
                  <div className="ref-stat-icon">👥</div>
                  <div className="ref-stat-info">
                    <div className="ref-stat-value">{userInfo.directReferrals || 0}</div>
                    <div className="ref-stat-label">Direct Referrals</div>
                  </div>
                </div>

                <div className="ref-stat">
                  <div className="ref-stat-icon">💰</div>
                  <div className="ref-stat-info">
                    <div className="ref-stat-value">{formatNumber(calculateNetworkEarnings(), 2)}</div>
                    <div className="ref-stat-label">Network Earnings</div>
                  </div>
                </div>
              </div>

              <div className="achievement-badge" style={{ borderColor: achievementInfo.color }}>
                <div className="achievement-icon">🏆</div>
                <div className="achievement-info">
                  <div className="achievement-level" style={{ color: achievementInfo.color }}>
                    {achievementInfo.name}
                  </div>
                  <div className="achievement-desc">{achievementInfo.requirement}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activities */}
          <div className="content-card recent-activities">
            <div className="card-header">
              <h3>Recent AI Activities</h3>
              <button className="view-all-btn" onClick={handleUpdateReturns}>
                View All
              </button>
            </div>

            <div className="activities-list">
              {recentActivities.length > 0 ? (
                recentActivities.slice(0, 10).map((activity, index) => {
                  const activityType = ACTIVITY_TYPES[activity.type] || { name: 'Unknown', icon: '❓', color: '#666' };
                  return (
                    <div key={index} className="activity-item">
                      <div className="activity-icon" style={{ backgroundColor: activityType.color }}>
                        {activityType.icon}
                      </div>
                      <div className="activity-details">
                        <div className="activity-name">{activityType.name}</div>
                        <div className="activity-time">{formatActivityTime(activity.timestamp)}</div>
                      </div>
                      <div className="activity-amount">
                        +{formatNumber(parseFloat(activity.amount) / 1e18, 2)} USDT
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-activities">
                  <div className="no-activities-icon">🤖</div>
                  <div className="no-activities-text">No recent activities detected</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;